import { hotelStatusNames, hotelSubsidiaryPmsHealthyKinds } from 'main/hotelStatuses'
import {
  NeedsAttentionProblemKeys,
  classifyNeedsAttentionProblems,
  readPmsSyncIsConnected
} from '../needsAttentionProblems'

/**
 *
 * @typedef Hotel
 * @property {string} hotelName
 * @property {string} bookingEngine
 * @property {string} chainCode
 * @property {string} parentChainCode
 * @property {string} textMarketingUrlKey
 * @property {string} addedOn
 * @property {number} addedBy
 * @property {string} updatedOn
 * @property {number} updatedBy
 * @property {string} id
 * @property {number} numberOfRooms
 * @property {string} pms
 * @property {boolean} isMissingSomeInformation
 * @property {number} displayId
 * @property {boolean} isDeleted
 * @property {string} status
 * @property {object[]} subsidiaryPmsUsers
 * @property {number} subsidiaryPmsUsers.id
 * @property {string} subsidiaryPmsUsers.subsidiaryId
 * @property {string} subsidiaryPmsUsers.email
 * @property {string} subsidiaryPmsUsers.firstName
 * @property {string} subsidiaryPmsUsers.lastName
 * @property {string} subsidiaryPmsUsers.birthDate
 * @property {string} subsidiaryPmsUsers.addedOn
 * @property {number} subsidiaryPmsUsers.addedBy
 * @property {string} merchantName
 * @property {boolean} inviteBackIsDisabled
 * @property {object[]} inviteBlockOn
 * @property {string} inviteBlockOn.addedBy
 * @property {string} inviteBlockOn.addedOn
 * @property {string} inviteBlockOn.reason
 * @property {boolean} pmsSyncIsConnected
 * @property {boolean} isServiceStarted
 * @property {boolean} isCampaignsEnabled
 * @property {boolean} isTcrActivated
 * @property {boolean} isPhoneEnabled
 * @property {object[]} tags
 * @property {number} tags.id
 * @property {string} tags.name
 * @property {string} tags.addedBy
 * @property {string} tags.addedOn
 * @property {string} processKind
 *
 *
 */

/**
 * True when at least one proxy line is Active (same rule as hotel details proxy numbers).
 * Uses API flag when present; supports PascalCase and string booleans from serializers.
 * @param {Hotel} hotel
 * @returns {boolean}
 */
export function readHasOperationalProxyPhone(hotel) {
  const lines = hotel.subsidiaryPhoneLines ?? hotel.SubsidiaryPhoneLines
  if (Array.isArray(lines) && lines.length > 0) {
    return lines.some(line => {
      const st = (line.status ?? line.Status ?? '').toString()
      return st === 'Active'
    })
  }

  const flag = hotel.isPhoneEnabled ?? hotel.IsPhoneEnabled
  if (flag === true || flag === 'true' || flag === 1) {
    return true
  }
  if (flag === false || flag === 'false' || flag === 0) {
    return false
  }
  return Boolean(flag)
}

/**
 * @param {Hotel} hotel
 * @returns {string}
 */
export function getStatusText(hotel) {
  return hotelStatusNames[hotel.status]
}

/**
 * @param {Hotel} hotel
 * @returns {string}
 */
export function getIssueText(hotel) {
  const tcrOn = hotel.isTcrActivated ?? hotel.IsTcrActivated
  if (tcrOn === false || tcrOn === 'false' || tcrOn === 0) {
    return 'TCR deactivated'
  } else if (!readHasOperationalProxyPhone(hotel)) {
    return 'Problem with phone'
  } else if (
    hotel.isEnabledPlatformAccessFee === false ||
    hotel.isEnabledPlatformAccessFee === 'false' ||
    hotel.isEnabledPlatformAccessFee === 0
  ) {
    return 'Platform access fee is not enabled'
  } else if (!readPmsSyncIsConnected(hotel)) {
    const problems = classifyNeedsAttentionProblems(hotel)
    if (problems.includes(NeedsAttentionProblemKeys.SyncStopped)) {
      return hotelSubsidiaryPmsHealthyKinds.SyncStopped
    }
    if (problems.includes(NeedsAttentionProblemKeys.NeverSynced)) {
      return hotelSubsidiaryPmsHealthyKinds.NotIntegrated || 'PMS is not integrated'
    }
    if (problems.includes(NeedsAttentionProblemKeys.PartiallySynced)) {
      return hotelSubsidiaryPmsHealthyKinds.PartiallyWorking
    }
    const kind = hotel.pmsHealthyKind
    const label = kind ? hotelSubsidiaryPmsHealthyKinds[kind] : null
    if (label) {
      return label
    }
    return 'PMS sync is not connected'
  } else if (hotel.inviteBlockOn) {
    return 'AutoInvite disabled'
  } else if (!hotel.isCampaignsEnabled) {
    return 'Campaigns are not enabled'
  } else if (!hotel.isServiceStarted) {
    return 'Services has not started'
  }

  return 'Up and Running'
}

/**
 *
 * @param {Hotel} hotel
 * @returns {string}
 */
export function getBrokenCtaText(hotel) {
  switch (hotel.status) {
    case 'PendingCampaignBrandRegistration':
      return 'Add tax info'
    case 'PendingPhoneNumberSetup':
      return 'Set up telecom'

    default:
      return 'View & fix'
  }
}
